import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import { paperPlaneOutline } from 'ionicons/icons';
import { useToast } from 'contexts/ToastContext';
import AuthService from '../../services/auth';
import Dropdown from '../../components/atoms/Dropdown/Dropdown';
import Field from '../../components/atoms/Field/Field';
import './ContactPage.css';

// Priority options for the contact form. Defined at module scope so
// they don't allocate per render. Matches the wording of the prior
// native <select> options.
const PRIORITY_OPTIONS = [
  { key: 'low',    label: 'Low — General question' },
  { key: 'medium', label: 'Medium — Need assistance' },
  { key: 'high',   label: 'High — Urgent issue' },
];

const ContactPage = () => {
  const location = useLocation();
  const [, setUser] = useState(null);

  const prefill = location.state?.prefill;
  const [formData, setFormData] = useState({
    subject: prefill === 'enterprise' ? 'Enterprise Plan Inquiry' : '',
    message: prefill === 'enterprise' 
      ? 'Hi, I\'m interested in the Enterprise plan.\n\nCompany name: \nCompany size: \nContact person: \nContact number: \nExpected monthly analyses: \nAnything else: ' 
      : '',
    priority: prefill === 'enterprise' ? 'medium' : 'medium'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get user info
  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      const currentUser = AuthService.getCurrentUser();
      setUser(currentUser);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Contact Us - Zibby</title>
        <meta name="description" content="Get in touch with the Zibby team for support and inquiries." />
      </Helmet>
      <ContactPageContent 
        formData={formData}
        setFormData={setFormData}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
      />
    </>
  );
};

const ContactPageContent = ({ formData, setFormData, isSubmitting, setIsSubmitting }) => {
  const { showToast } = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // This is a PUBLIC support page (no login). We route the message straight to
    // support@zibby.dev via the user's mail client — no API call (there is no
    // public /contact endpoint, and a public unauthenticated POST would fail CORS
    // anyway). The email address is also shown in the header as a no-JS fallback.
    const subject = encodeURIComponent(formData.subject || 'Zibby support request');
    const body = encodeURIComponent(
      `${formData.message || ''}\n\n— \nPriority: ${formData.priority || 'medium'}`
    );
    window.location.href = `mailto:support@zibby.dev?subject=${subject}&body=${body}`;
    showToast('Opening your email app to send to support@zibby.dev…', 'success');
  };

  return (
    <div className="contact-page page-container-md page-fade-in main-body">
        <div className="page-header-center">
          <h1>Contact &amp; Support</h1>
          <p>
            Need help? Email us at{' '}
            <a href="mailto:support@zibby.dev">support@zibby.dev</a>{' '}
            — we respond within 2 business days. No account required. Or use the form below.
          </p>
        </div>

        <div className="page-layout main-body">
          <div className="contact-form-section">
            <div className="page-card">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="subject" className="form-label">Subject</label>
                  <Field
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="What can we help you with?"
                    autoComplete="off"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="priority" className="form-label">Priority</label>
                  {/* Use the atomic Dropdown component (same one the
                      marketplace wizard uses) instead of a native
                      <select> — keeps the dark theme + chevron
                      consistent across forms. */}
                  <Dropdown
                    className="contact-dropdown"
                    options={PRIORITY_OPTIONS}
                    value={formData.priority}
                    onChange={(value) =>
                      handleInputChange({ target: { name: 'priority', value } })
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message</label>
                  <Field
                    as="textarea"
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Please describe your question or issue in detail..."
                    rows="12"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-full-width"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <IonIcon icon={paperPlaneOutline} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
    </div>
  );
};

export default ContactPage;
